import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { publicAPI } from '../../services/api';

declare global {
  interface Window {
    dataLayer: any[];
    google_tag_manager: any;
  }
}

interface GTMConfig {
  container_id: string;
  container_name?: string;
  environment?: string;
  auth_token?: string;
  preview_token?: string;
  enable_enhanced_ecommerce?: boolean;
}

const GoogleTagManager: React.FC = () => {
  const location = useLocation();
  const [gtmConfig, setGtmConfig] = useState<GTMConfig | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Fetch active GTM config on mount
  useEffect(() => {
    const fetchGTMConfig = async () => {
      // Check localStorage first for instant load
      const cachedConfig = localStorage.getItem('gtm_config');
      if (cachedConfig) {
        try {
          setGtmConfig(JSON.parse(cachedConfig));
        } catch (e) {
          localStorage.removeItem('gtm_config');
        }
      }

      try {
        const data = await publicAPI.getActiveGTM();
        if (data && data.container_id) {
          setGtmConfig(data);
          localStorage.setItem('gtm_config', JSON.stringify(data));
        }
      } catch (error) {
        console.error('Failed to fetch GTM configuration:', error);
      }
    };
    fetchGTMConfig();
  }, []);

  // Initialize GTM when config is available
  useEffect(() => {
    if (!gtmConfig || !gtmConfig.container_id) return;
    if (initialized) return;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Build GTM script URL
    let gtmUrl = `https://www.googletagmanager.com/gtm.js?id=${gtmConfig.container_id}`;
    
    // Add environment parameters if custom environment
    if (gtmConfig.environment === 'CUSTOM' && gtmConfig.auth_token && gtmConfig.preview_token) {
      gtmUrl += `&gtm_auth=${gtmConfig.auth_token}&gtm_preview=${gtmConfig.preview_token}&gtm_cookies_win=x`;
    }

    // Inject GTM script
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      '${gtmUrl}';f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmConfig.container_id}');
    `;
    document.head.appendChild(script);

    // Add noscript iframe to body
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    let iframeSrc = `https://www.googletagmanager.com/ns.html?id=${gtmConfig.container_id}`;
    if (gtmConfig.environment === 'CUSTOM' && gtmConfig.auth_token && gtmConfig.preview_token) {
      iframeSrc += `&gtm_auth=${gtmConfig.auth_token}&gtm_preview=${gtmConfig.preview_token}&gtm_cookies_win=x`;
    }
    iframe.src = iframeSrc;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    console.log(`GTM initialized with container: ${gtmConfig.container_id}`);
    setInitialized(true);
  }, [gtmConfig, initialized]);

  // Track page views on route change
  useEffect(() => {
    if (initialized && window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location, initialized]);

  return null; // Component renders nothing
};

export default GoogleTagManager;
